import { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { signIn, confirmSignIn } from "aws-amplify/auth";
import { COGNITO_ENABLED, STAFF_DOMAINS } from "../config";
import { useAppAuth } from "../auth";
import { AuthLayout } from "../components/AuthLayout";

const ARROW = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

// Cognito sign-in (custom form). Staff accounts are admin-created, so beyond credentials this
// handles the two staff-relevant challenges: TOTP step-up (CONFIRM_SIGN_IN_WITH_TOTP_CODE) and the
// forced password change on first sign-in of an admin-created account
// (CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED). On success the bridge's Hub listener flips the
// context and the isSignedIn redirect takes over; the /v1/doctor/me probe then authorizes.
function CognitoSignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState("");
  const [step, setStep] = useState<"credentials" | "totp" | "newPassword">("credentials");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function routeNextStep(signInStep: string) {
    if (signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
      setChallenge("");
      setStep("totp");
    } else if (signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
      setChallenge("");
      setStep("newPassword");
    } else if (signInStep !== "DONE") {
      setError("This account requires a sign-in step this portal doesn’t support. Contact your administrator.");
      setStep("credentials");
    }
  }

  async function submitCredentials(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { nextStep } = await signIn({ username: email.trim(), password });
      routeNextStep(nextStep.signInStep);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function submitChallenge(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { nextStep } = await confirmSignIn({ challengeResponse: challenge });
      routeNextStep(nextStep.signInStep);
    } catch (err) {
      setError(err instanceof Error ? err.message : "That didn’t work. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (step === "totp") {
    return (
      <AuthLayout>
        <form className="auth-form" onSubmit={submitChallenge}>
          <h2>
            Two-factor <em>check.</em>
          </h2>
          <p>Enter the 6-digit code from your authenticator app.</p>
          <div className="input-group">
            <label htmlFor="auth-totp">Authenticator code</label>
            <input
              id="auth-totp"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="123456"
              value={challenge}
              onChange={(e) => setChallenge(e.target.value.replace(/\D/g, ""))}
              required
            />
          </div>
          {error && (
            <div className="auth-meta" role="alert" style={{ color: "var(--red)" }}>{error}</div>
          )}
          <button type="submit" className="btn btn-primary btn-lg" disabled={busy || challenge.length < 6}>
            {busy ? "Verifying…" : "Verify"}
            {!busy && ARROW}
          </button>
          <div className="auth-meta">
            <button type="button" className="auth-link-btn" onClick={() => setStep("credentials")} style={{ background: "none", border: 0, padding: 0, color: "inherit", textDecoration: "underline", cursor: "pointer" }}>
              &larr; Back to sign in
            </button>
          </div>
        </form>
      </AuthLayout>
    );
  }

  if (step === "newPassword") {
    return (
      <AuthLayout>
        <form className="auth-form" onSubmit={submitChallenge}>
          <h2>
            Set a new <em>password.</em>
          </h2>
          <p>Your account was created by an administrator — choose your own password to continue.</p>
          <div className="input-group">
            <label htmlFor="auth-newpw">New password</label>
            <input
              id="auth-newpw"
              type="password"
              autoComplete="new-password"
              placeholder="12+ characters"
              value={challenge}
              onChange={(e) => setChallenge(e.target.value)}
              required
              minLength={12}
            />
          </div>
          {error && (
            <div className="auth-meta" role="alert" style={{ color: "var(--red)" }}>{error}</div>
          )}
          <button type="submit" className="btn btn-primary btn-lg" disabled={busy}>
            {busy ? "Saving…" : "Set password & continue"}
            {!busy && ARROW}
          </button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout tab="signin">
      <form className="auth-form" onSubmit={submitCredentials}>
        <h2>
          Welcome <em>back.</em>
        </h2>
        <p>Sign in to continue caring for your patients.</p>
        <p className="auth-meta auth-domain-hint">
          Use your {STAFF_DOMAINS.join(" or ")} work account.
        </p>
        <div className="input-group">
          <label htmlFor="auth-email">Email address</label>
          <input
            id="auth-email"
            type="email"
            autoComplete="email"
            placeholder="dr.you@championcare.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="input-group">
          <label htmlFor="auth-password">Password</label>
          <input
            id="auth-password"
            type="password"
            autoComplete="current-password"
            placeholder="••••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
        {error && (
          <div className="auth-meta" role="alert" style={{ color: "var(--red)" }}>{error}</div>
        )}
        <button type="submit" className="btn btn-primary btn-lg" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
          {!busy && ARROW}
        </button>
        <div className="auth-meta">
          Forgot your password? <Link to="/reset-password">Reset it</Link>
        </div>
      </form>
    </AuthLayout>
  );
}

export function SignIn() {
  const { isLoaded, isSignedIn, devSignIn } = useAppAuth();
  const [busy, setBusy] = useState(false);

  if (isLoaded && isSignedIn) return <Navigate to="/" replace />;

  if (COGNITO_ENABLED) return <CognitoSignIn />;

  async function continueDevSession(e?: React.FormEvent) {
    e?.preventDefault();
    setBusy(true);
    await devSignIn?.();
    setBusy(false);
  }

  return (
    <AuthLayout tab="signin">
      <form className="auth-form" onSubmit={continueDevSession}>
        <h2>
          Welcome <em>back.</em>
        </h2>
        <p>Sign in to continue caring for your patients.</p>
        <div className="input-group">
          <label htmlFor="auth-email">Email address</label>
          <input id="auth-email" type="email" placeholder="dr.you@championcare.com" defaultValue="dr.forester@championcare.com" />
        </div>
        <div className="input-group">
          <label htmlFor="auth-password">Password</label>
          <input id="auth-password" type="password" placeholder="••••••••••" defaultValue="demo-password" />
        </div>
        <button type="submit" className="btn btn-primary btn-lg" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
          {!busy && ARROW}
        </button>
        <div className="auth-meta">
          Forgot your password? <Link to="/reset-password">Reset it</Link>
        </div>
        <div className="auth-meta">
          Cognito (with MFA) isn&rsquo;t configured here — this continues with a local dev session.
        </div>
      </form>
    </AuthLayout>
  );
}
